<template>
  <!-- Retorno de 18/10 (card 17trt6jcyfb): "games nessa aula". Jogos de
       outras plataformas (Wordwall e parecidas) cadastrados no
       administrativo. A API so devolve URL de plataforma liberada; o iframe e
       montado aqui, nunca a partir de HTML colado. Some quando a aula nao tem
       jogo.

       Retorno de 22/10: o bloco lista so os nomes, e o jogo abre (um por vez)
       ao clicar no nome. O Kahoot nao se joga incorporado: tem "Jogar Kahoot"
       (kahoot.it, com o PIN) e "Apresentacao do
       Professor" (o aluno tambem, para praticar sozinho). O aluno marca
       cada jogo como concluido. -->
  <section
    v-if="games.length"
    class="lgm"
    :class="[`lgm--${audience}`, { 'is-open': open }]"
  >
    <button
      type="button"
      class="lgm__toggle"
      :aria-expanded="String(open)"
      aria-controls="lgm-body"
      @click="open = !open"
    >
      <span class="lgm__title">
        <i class="mdi mdi-gamepad-variant-outline" aria-hidden="true"></i>
        {{ $t("games.title") }}
      </span>
      <span class="lgm__count">
        <template v-if="isStudent">
          {{ $t("games.countDone", { done: doneCount, count: games.length }) }}
        </template>
        <template v-else>
          {{ $tc("games.count", games.length, { count: games.length }) }}
        </template>
      </span>
      <i
        class="mdi lgm__chev"
        :class="open ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        aria-hidden="true"
      ></i>
    </button>

    <ul v-if="open" id="lgm-body" class="lgm__list">
      <li
        v-for="game in games"
        :key="game.id"
        class="lgm__row"
        :class="{ 'is-active': activeId === game.id }"
      >
        <div class="lgm__line">
          <label v-if="isStudent" class="lgm__check">
            <input
              type="checkbox"
              :checked="!!game.completed"
              :disabled="savingId === game.id"
              :aria-label="$t('games.markDone')"
              @change="toggleDone(game, $event.target.checked)"
            />
            <span class="lgm__box" aria-hidden="true">
              <i v-if="game.completed" class="mdi mdi-check"></i>
            </span>
          </label>

          <!-- Kahoot: botões no lugar do jogo incorporado. -->
          <template v-if="game.provider === 'kahoot'">
            <span class="lgm__name">{{ game.title }}</span>
            <span class="lgm__actions">
              <a
                v-if="game.presentationUrl"
                :href="game.presentationUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="lgm__btn lgm__btn--ghost"
              >
                {{ $t("games.kahootPresent") }}
              </a>
              <a
                :href="game.playUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="lgm__btn"
                :title="$t('games.kahootHint')"
              >
                {{ $t("games.kahootPlay") }}
              </a>
            </span>
          </template>

          <template v-else>
            <button
              type="button"
              class="lgm__name lgm__name--link"
              :aria-expanded="String(activeId === game.id)"
              @click="toggleGame(game)"
            >
              {{ game.title }}
            </button>
            <span class="lgm__actions">
              <button type="button" class="lgm__btn" @click="toggleGame(game)">
                {{ activeId === game.id ? $t("games.hide") : $t("games.play") }}
              </button>
            </span>
          </template>
        </div>

        <!-- Um jogo aberto por vez, logo abaixo do nome. -->
        <div
          v-if="activeId === game.id && game.provider !== 'kahoot'"
          class="lgm__stage"
        >
          <div class="lgm__frame">
            <iframe
              :src="game.url"
              :title="game.title"
              loading="lazy"
              allowfullscreen
              referrerpolicy="strict-origin-when-cross-origin"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
            ></iframe>
          </div>
          <a
            :href="game.url"
            target="_blank"
            rel="noopener noreferrer"
            class="lgm__out"
          >
            <i class="mdi mdi-open-in-new" aria-hidden="true"></i>
            {{ $t("games.openNewTab") }}
          </a>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import pitchPartnerApi from "@/shared/pitchPartnerApi";

export default {
  name: "LessonGames",
  props: {
    courseClassId: { type: String, required: true },
    // customer (aula do aluno) | partner (aula do professor)
    audience: { type: String, default: "customer" },
  },
  data() {
    return { games: [], open: false, activeId: null, savingId: null };
  },
  computed: {
    isStudent() {
      return this.audience === "customer";
    },
    doneCount() {
      return this.games.filter((game) => game.completed).length;
    },
  },
  watch: {
    courseClassId: {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.activeId = null;
        const request =
          this.audience === "partner"
            ? pitchPartnerApi.getLessonGames(id)
            : customerApi.listLessonGames(id);
        request
          .then((res) => {
            this.games = res.data?.data || [];
          })
          // Sem jogos, a aula segue normal: o bloco so nao aparece.
          .catch(() => {
            this.games = [];
          });
      },
    },
  },
  methods: {
    toggleGame(game) {
      this.activeId = this.activeId === game.id ? null : game.id;
    },
    toggleDone(game, done) {
      const previous = !!game.completed;
      this.$set(game, "completed", done);
      this.savingId = game.id;
      customerApi
        .setGameCompletion(game.id, done)
        .catch(() => {
          this.$set(game, "completed", previous);
          this.$root.$emit("display-alert", {
            text: this.$t("games.saveFailed"),
            type: 1,
          });
        })
        .finally(() => {
          this.savingId = null;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Aluno em rosa, professor em roxo: a paleta de cada um. */
.lgm {
  --accent: var(--dk-pink, #e8276f);
  --ink: var(--dk-ink, #2b2540);
  --muted: var(--dk-muted, #7a7590);

  grid-column: 1 / -1;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--dk-line, #ece6f5);
  font-family: "Poppins", sans-serif;
}

.lgm--partner {
  --accent: var(--dk-grape, #a826e1);
  margin-top: 16px;
}

.lgm__toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 20px;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.lgm__toggle:focus-visible {
  outline: 3px solid rgba(43, 37, 64, 0.2);
  outline-offset: -3px;
  border-radius: 16px;
}

.lgm__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
}

.lgm__title .mdi {
  color: var(--accent);
}

.lgm__count {
  margin-left: auto;
  font-size: 13px;
  color: var(--muted);
}

.lgm__chev {
  font-size: 22px;
  color: var(--muted);
}

/* Retorno de 22/10: lista de nomes; o jogo abre em cascata. */
.lgm__list {
  list-style: none;
  margin: 0;
  padding: 0 20px 16px;
}

.lgm__row + .lgm__row {
  border-top: 1px solid var(--dk-line, #f1ecf7);
}

.lgm__line {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
}

.lgm__check {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  cursor: pointer;
}

.lgm__check input {
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: pointer;
}

.lgm__box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--dk-line-strong, #d9d1e6);
  color: #fff;
  font-size: 15px;
  transition: background 0.15s, border-color 0.15s;
}

.lgm__check input:checked + .lgm__box {
  background: var(--accent);
  border-color: var(--accent);
}

.lgm__check input:focus-visible + .lgm__box {
  outline: 3px solid rgba(232, 39, 111, 0.3);
  outline-offset: 2px;
}

.lgm__name {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}

.lgm__name--link {
  border: 0;
  padding: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
}

.lgm__name--link:hover {
  color: var(--accent);
}

.lgm__actions {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.lgm__btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--accent);
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}

.lgm__btn--ghost {
  background: transparent;
  color: var(--accent);
}

.lgm__stage {
  display: grid;
  gap: 8px;
  padding: 0 0 16px;
}

.lgm__out {
  font-size: 13px;
  color: var(--accent);
  text-decoration: none;
}

.lgm__out:hover {
  text-decoration: underline;
}

/* Os jogos do Wordwall vem em 500x380; a proporcao 4:3 serve para todos. */
.lgm__frame {
  position: relative;
  width: 100%;
  max-width: 760px;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  overflow: hidden;
  background: var(--dk-wash, #f6f2fb);
}

.lgm__frame iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
